import React, { useEffect, useState } from "react";
import { useWaProvider } from "../lib/waProvider";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import DatePickerInput from "../components/DatePickerInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Megaphone, Plus, Pencil, Trash2, BarChart3, Users, Ticket, Rocket, TrendingUp, Copy,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import WaSendQueue from "../components/WaSendQueue";
import InitiativeSheetEditor from "../components/InitiativeSheetEditor";
import InitiativeShare from "../components/InitiativeShare";
import CampaignCloneDialog from "../components/CampaignCloneDialog";
import { useAppSettings } from "../context/AppSettingsContext";
import { smsTemplateStats } from "../lib/sms";

const fmtIt = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "");
const fmtShort = (d) => (d ? d.slice(5, 10).split("-").reverse().join("/") : "");
const countdownOf = (c) => {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const days = (d) => Math.round((new Date(d.slice(0, 10) + "T00:00:00") - t) / 86400000);
  if (!c.active) return "";
  const s = days(c.starts_at || ""), e = days(c.ends_at || "");
  if (Number.isNaN(s) || Number.isNaN(e) || e < 0) return "";
  if (s > 0) return s === 1 ? "Inizia domani" : `Inizia tra ${s} giorni`;
  return e === 0 ? "Termina oggi" : e === 1 ? "Termina domani" : `Termina tra ${e} giorni`;
};

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = {
  name: "", description: "", multiplier: 2,
  starts_at: today(), ends_at: today(), active: true,
  segment_id: "", coupon_id: "", collection_id: "", wa_body: "", sms_body: "",
  channel: "whatsapp",   // whatsapp | email | both | sms
  scheduled_at: "",       // datetime-local per invio differito
};

function KPI({ label, value, suffix = "", accent = "gold" }) {
  const color = accent === "red" ? "text-red-300" :
                accent === "mint" ? "text-emerald-300" :
                accent === "blue" ? "text-indigo-300" :
                "text-amber-300";
  return (
    <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-heading font-bold text-xl ${color}`}>{value}<span className="text-xs text-zinc-500 ml-1">{suffix}</span></div>
    </div>
  );
}

function fmtPct(v) {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v}%`;
}

export default function Campaigns() {
  const { label: waLabel } = useWaProvider();
  const { settings } = useAppSettings();
  const [sheetFor, setSheetFor] = useState(null);
  const [cloneFor, setCloneFor] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [segments, setSegments] = useState([]);
  const [collections, setCollections] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [segmentFilter, setSegmentFilter] = useState("all");
  const [adminNotifs, setAdminNotifs] = useState({ unread: 0, notifications: [] });
  const [notifsExpanded, setNotifsExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [overlap, setOverlap] = useState(null);
  const [overlapAck, setOverlapAck] = useState(false);
  const [overlapPending, setOverlapPending] = useState(false);
  useEffect(() => {
    if (!open || !form.starts_at || !form.ends_at || !form.active || editing?.product_scope === "products") { setOverlap(null); setOverlapPending(false); return; }
    setOverlapAck(false);
    setOverlapPending(true);
    const h = setTimeout(() => {
      api.get("/campaigns/overlap", { params: { starts_at: form.starts_at, ends_at: form.ends_at, multiplier: form.multiplier || 1, exclude_id: editing?.id || undefined, collection_id: form.collection_id || undefined } })
        .then((r) => setOverlap(r.data.items.length ? r.data : null)).catch(() => setOverlap(null)).finally(() => setOverlapPending(false));
    }, 350);
    return () => clearTimeout(h);
  }, [open, form.starts_at, form.ends_at, form.multiplier, form.active, form.collection_id, editing]);
  const [launchResult, setLaunchResult] = useState(null);
  const [smsRefreshing, setSmsRefreshing] = useState(null);
  const refreshSmsStatus = async (c) => {
    setSmsRefreshing(c.id);
    try {
      const { data } = await api.get(`/campaigns/${c.id}/sms-status`);
      const d = data.delivery;
      toast.success(`SMS consegnati ${d.delivered}/${d.total}${d.error ? ` · ${d.error} errori` : ""}${d.waiting ? ` · ${d.waiting} in attesa` : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSmsRefreshing(null); }
  };
  const [waQueue, setWaQueue] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const load = async () => {
    const [c, s, cp, col] = await Promise.all([
      api.get("/campaigns"),
      api.get("/segments").catch(() => ({ data: [] })),
      api.get("/coupons").catch(() => ({ data: [] })),
      api.get("/collections").catch(() => ({ data: [] })),
    ]);
    setItems(c.data);
    setSegments(s.data);
    setCoupons(cp.data);
    setCollections(col.data);
  };
  useEffect(() => { load(); loadNotifs(); }, []);
  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/admin/notifications", { params: { limit: 20 } });
      setAdminNotifs(data || { unread: 0, notifications: [] });
    } catch { /* silent */ }
  };
  const markAllRead = async () => {
    try {
      await api.post("/admin/notifications/read-all");
      setAdminNotifs({ ...adminNotifs, unread: 0 });
      toast.success("Notifiche segnate come lette");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async () => {
    const payload = { ...form };
    if (!payload.segment_id) payload.segment_id = null;
    if (!payload.collection_id) payload.collection_id = null;
    if (!payload.coupon_id) payload.coupon_id = null;
    if (!payload.wa_body) payload.wa_body = null;
    if (!payload.sms_body) payload.sms_body = null;
    if (payload.channel === "sms") {
      const st = smsTemplateStats(payload.sms_body || "");
      if (!payload.sms_body) return toast.error("Inserisci il testo dell'SMS");
      if (!st.ok) return toast.error(`SMS troppo lungo: ${st.length}/${st.limit} caratteri`);
    }
    if (overlapPending) return toast.info("Verifica sovrapposizioni in corso, riprova tra un attimo");
    if (overlap?.loses && !overlapAck) {
      setOverlapAck(true);
      return toast.warning("Attenzione: in cassa vincerà un'altra campagna nei giorni sovrapposti. Premi di nuovo per salvare comunque.");
    }
    try {
      if (editing) await api.patch(`/campaigns/${editing.id}`, payload);
      else await api.post("/campaigns", payload);
      setOpen(false);
      toast.success("Campagna salvata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la campagna?")) return;
    await api.delete(`/campaigns/${id}`);
    toast.success("Eliminata");
    load();
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", description: c.description || "",
      multiplier: c.multiplier || 1, starts_at: c.starts_at, ends_at: c.ends_at,
      active: c.active !== false,
      segment_id: c.segment_id || "", coupon_id: c.coupon_id || "",
      collection_id: c.collection_id || "",
      wa_body: c.wa_body || "",
      sms_body: c.sms_body || "",
    });
    setOpen(true);
  };

  const launch = async (c) => {
    if (!c.segment_id) return toast.error("Collega prima un cluster");
    if (!window.confirm(`Lanciare la campagna "${c.name}"? Verranno generati i link WhatsApp e assegnato il coupon (se presente).`)) return;
    try {
      const { data } = await api.post(`/campaigns/${c.id}/launch`);
      setLaunchResult({ ...data, campaign: c });
      if (data.via_sms) {
        toast.success(`Campagna SMS inviata · ${data.sms?.sent || 0} SMS accettati${data.sms?.invalid ? `, ${data.sms.invalid} numeri non validi` : ""}${data.sms?.credits != null ? ` · crediti residui ${data.sms.credits}` : ""}`);
      } else if (data.via_spoki) {
        toast.success(`Campagna avviata via Spoki · ${data.spoki?.accepted || 0} inviati${data.spoki?.failed ? `, ${data.spoki.failed} falliti` : ""}`);
      } else {
        toast.success(`Campagna avviata · ${data.count} messaggi pronti`);
        setWaQueue({ messages: data.messages });
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openAnalytics = async (c) => {
    setAnalytics({ campaign: c, loading: true });
    setAnalyticsLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${c.id}/analytics`);
      setAnalytics(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setAnalytics(null);
    } finally { setAnalyticsLoading(false); }
  };

  const isActiveNow = (c) => {
    const t = today();
    return c.active && c.starts_at <= t && c.ends_at >= t;
  };

  const segmentName = (id) => segments.find((s) => s.id === id)?.name;
  const couponName = (id) => coupons.find((c) => c.id === id)?.name;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="campaigns-page">
      {sheetFor && <InitiativeSheetEditor campaign={sheetFor} open={!!sheetFor} onClose={() => { setSheetFor(null); load(); }} />}
      {cloneFor && <CampaignCloneDialog campaign={cloneFor} open={!!cloneFor} onClose={() => setCloneFor(null)} onCloned={(nc) => { load(); openEdit(nc); }} />}
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red">CAMPAGNE</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Moltiplicatori & promozioni</h1>
          <p className="text-zinc-400 mt-1">Collega cluster e coupon, lancia broadcast e analizza i risultati.</p>
        </div>
        {isAdmin && (
          <Button data-testid="campaign-new-button" onClick={openNew} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
            <Plus className="h-4 w-4 mr-2" /> Nuova campagna
          </Button>
        )}
      </div>

      {adminNotifs.notifications.length > 0 && (
        <div
          data-testid="admin-notifs-banner"
          className={`card-elev p-3 border-l-4 ${adminNotifs.unread > 0 ? "border-indigo-500" : "border-zinc-700"}`}
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setNotifsExpanded((v) => !v)}
              data-testid="admin-notifs-toggle"
              className="flex items-center gap-2 text-sm hover:text-white transition"
            >
              <span className="text-lg">🔔</span>
              <span className="font-heading font-semibold">Notifiche admin</span>
              {adminNotifs.unread > 0 && (
                <span data-testid="admin-notifs-unread-badge" className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500 text-white font-bold">
                  {adminNotifs.unread} nuove
                </span>
              )}
              <span className="text-zinc-500 text-xs">{notifsExpanded ? "▲" : "▼"}</span>
            </button>
            {adminNotifs.unread > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={markAllRead}
                data-testid="admin-notifs-mark-read"
                className="border-indigo-800/50 text-indigo-300 h-7 text-[11px]"
              >
                Segna tutte come lette
              </Button>
            )}
          </div>
          {notifsExpanded && (
            <ul className="mt-3 space-y-1.5 max-h-64 overflow-y-auto" data-testid="admin-notifs-list">
              {adminNotifs.notifications.map((n) => (
                <li
                  key={n.id}
                  data-testid={`admin-notif-${n.id}`}
                  className={`text-xs p-2 rounded ${n.read_at ? "bg-zinc-950 text-zinc-500" : "bg-indigo-500/10 text-indigo-200 border border-indigo-500/30"}`}
                >
                  <div className="font-semibold">{n.title}</div>
                  <div className="text-[11px] opacity-80">{n.body}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{new Date(n.created_at).toLocaleString("it-IT")}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {segments.length > 0 && items.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="campaigns-segment-filter">
          <button
            type="button"
            onClick={() => setSegmentFilter("all")}
            data-testid="campaigns-segment-filter-all"
            className={`text-[11px] px-3 py-1 rounded-full border transition ${segmentFilter === "all" ? "bg-zinc-700 text-white border-zinc-600" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
          >
            Tutti i cluster <span className="opacity-70 ml-1">({items.length})</span>
          </button>
          {segments.map((s) => {
            const count = items.filter((c) => c.segment_id === s.id).length;
            if (count === 0) return null;
            const on = segmentFilter === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSegmentFilter(s.id)}
                data-testid={`campaigns-segment-filter-${s.id}`}
                className={`text-[11px] px-3 py-1 rounded-full border transition ${on ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
              >
                {s.name} <span className="opacity-70 ml-1">({count})</span>
              </button>
            );
          })}
          {items.some((c) => !c.segment_id) && (
            <button
              type="button"
              onClick={() => setSegmentFilter("__none__")}
              data-testid="campaigns-segment-filter-none"
              className={`text-[11px] px-3 py-1 rounded-full border transition ${segmentFilter === "__none__" ? "bg-red-500/20 text-red-300 border-red-500/40" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
            >
              Senza cluster <span className="opacity-70 ml-1">({items.filter((c) => !c.segment_id).length})</span>
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 && <div className="text-zinc-500 col-span-full">Nessuna campagna creata.</div>}
        {items.filter((c) => segmentFilter === "all" || (c.segment_id || "__none__") === segmentFilter).map((c) => (
          <div key={c.id} data-testid={`campaign-card-${c.id}`} className={`card-elev p-5 space-y-3 ${isActiveNow(c) ? "glow-gold" : ""}`}>
            <div className="flex flex-wrap justify-end items-center gap-2">
              {c.sheet?.publish_landing && c.sheet?.slug && <InitiativeShare compact idSuffix={`-${c.id}`} appName={settings?.app_name} d={{ id: c.id, slug: c.sheet.slug, publish_landing: true, headline: c.sheet.headline || `x${c.multiplier || 1} Punti`, subtitle: c.sheet.subtitle, period: `Dal ${fmtShort(c.starts_at)} al ${fmtShort(c.ends_at)}`, countdown: countdownOf(c) }} />}
              <Button size="sm" variant="outline" className="border-zinc-800 text-xs" onClick={() => setSheetFor(c)} data-testid={`campaign-sheet-${c.id}`}>📄 Scheda iniziativa{c.sheet?.publish_home || c.sheet?.publish_landing ? " · pubblicata" : ""}</Button>
            </div>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-300">
                  <Megaphone className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-heading font-bold text-lg">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{c.starts_at} → {c.ends_at}</div>
                </div>
              </div>
              <span className={`pill ${isActiveNow(c) ? "pill-gold" : c.active ? "pill-mint" : "pill-muted"}`}>
                {isActiveNow(c) ? "In corso" : c.active ? "Programmata" : "Off"}
              </span>
            </div>
            {c.description && <p className="text-sm text-zinc-400">{c.description}</p>}

            <div className="flex flex-wrap gap-1.5 text-[11px]">
              <span className="pill pill-gold font-mono">{c.multiplier}× punti</span>
              {c.collection_id && collections.find((x) => x.id === c.collection_id) && <span className="pill text-[10px]" style={{ background: collections.find((x) => x.id === c.collection_id).color, color: "#111" }} data-testid={`campaign-collection-${c.id}`}>Raccolta {collections.find((x) => x.id === c.collection_id).name}</span>}
              {c.segment_id && <span className="pill pill-muted"><Users className="h-3 w-3" /> {segmentName(c.segment_id) || "cluster"}</span>}
              {c.coupon_id && <span className="pill pill-muted"><Ticket className="h-3 w-3" /> {couponName(c.coupon_id) || "coupon"}</span>}
              {c.last_launched_at && (
                <span className="pill pill-muted text-emerald-300">
                  Ultimo lancio: {new Date(c.last_launched_at).toLocaleDateString("it-IT")} ({c.last_launched_count || 0})
                </span>
              )}
              {c.last_sms && (
                <span className="pill pill-sky flex items-center gap-1" data-testid={`campaign-sms-status-${c.id}`} title={c.last_sms.delivery?.checked_at ? `Aggiornato ${new Date(c.last_sms.delivery.checked_at).toLocaleString("it-IT")}` : "Clicca ↻ per leggere il report di consegna SMS Factor"}>
                  SMS {c.last_sms.sent || 0} inviati
                  {c.last_sms.delivery && <> · <b>{c.last_sms.delivery.delivered}</b> consegnati{c.last_sms.delivery.error ? ` · ${c.last_sms.delivery.error} errori` : ""}{c.last_sms.delivery.waiting ? ` · ${c.last_sms.delivery.waiting} in attesa` : ""}{c.last_sms.delivery.expired ? ` · ${c.last_sms.delivery.expired} scaduti` : ""}</>}
                  <button type="button" onClick={(e) => { e.stopPropagation(); refreshSmsStatus(c); }} disabled={smsRefreshing === c.id} data-testid={`campaign-sms-refresh-${c.id}`} className="ml-1 hover:rotate-90 transition-transform disabled:opacity-50" title="Aggiorna report consegna">↻</button>
                </span>
              )}
              {c.last_launched_source === "scheduled" && (
                <span
                  data-testid={`campaign-source-scheduled-${c.id}`}
                  className="pill text-[10px] bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                  title="Partita automaticamente dal cron scheduled-campaigns"
                >
                  ⏱️ Auto-lancio
                </span>
              )}
              {c.scheduled_at && !c.scheduled_launched_at && (
                <span
                  data-testid={`campaign-scheduled-pending-${c.id}`}
                  className="pill text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/40"
                  title={`Partirà automaticamente il ${new Date(c.scheduled_at).toLocaleString("it-IT")}`}
                >
                  📅 Programmata: {new Date(c.scheduled_at).toLocaleDateString("it-IT")}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2 border-t border-zinc-900 pt-3">
              {c.segment_id && (
                <Button data-testid={`campaign-launch-${c.id}`} size="sm" onClick={() => launch(c)} className="bg-[color:var(--whatsapp)] text-black hover:opacity-90 h-9">
                  <Rocket className="h-3.5 w-3.5 mr-1" /> Lancia
                </Button>
              )}
              <Button data-testid={`campaign-analytics-${c.id}`} size="sm" variant="outline" onClick={() => openAnalytics(c)} className="border-zinc-800 h-9">
                <BarChart3 className="h-3.5 w-3.5 mr-1" /> Analizza
              </Button>
              {isAdmin && (
                <>
                  <Button data-testid={`campaign-edit-${c.id}`} variant="outline" size="sm" className="border-zinc-800 h-9" onClick={() => openEdit(c)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`campaign-clone-${c.id}`} variant="outline" size="sm" className="border-zinc-800 h-9" title="Duplica iniziativa" onClick={() => setCloneFor(c)}><Copy className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`campaign-delete-${c.id}`} variant="outline" size="sm" className="border-zinc-800 hover:border-red-500 hover:text-red-300 h-9" onClick={() => remove(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* CREATE/EDIT */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica campagna" : "Nuova campagna"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300 mb-1 inline-block">Nome*</Label>
              <Input data-testid="campaign-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div><Label className="text-zinc-300 mb-1 inline-block">Descrizione</Label>
              <Textarea data-testid="campaign-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 [&>div]:min-w-0">
              <div><Label className="text-zinc-300 mb-1 inline-block">Moltiplicatore</Label>
                <Input data-testid="campaign-form-multiplier" type="number" step="0.1" value={form.multiplier} onChange={(e) => setForm({ ...form, multiplier: parseFloat(e.target.value || "1") })} className="bg-zinc-950 border-zinc-800" />
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">Inizio</Label>
                <DatePickerInput testid="campaign-form-start" value={form.starts_at} max={form.ends_at || undefined} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">Fine</Label>
                <DatePickerInput testid="campaign-form-end" value={form.ends_at} min={form.starts_at || undefined} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
              </div>
            </div>
            {overlap && (
              <div className={`rounded-lg border p-3 text-xs space-y-1 ${overlap.loses ? "border-amber-500 bg-amber-100 text-amber-900 dark:bg-amber-500/10 dark:text-amber-100 dark:border-amber-600/60" : "border-zinc-300 bg-zinc-100 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-300"}`} data-testid="campaign-overlap-warning">
                <div className="font-semibold">{overlap.loses ? "⚠️ Sovrapposizione: in cassa vince un'altra campagna" : "ℹ️ Sovrapposizione con altre campagne: questa vince in cassa"}</div>
                <div className="opacity-80">Sui giorni in comune si applica un solo moltiplicatore "su tutta la spesa": il più alto (a parità, la campagna più vecchia).</div>
                <ul className="space-y-0.5">
                  {overlap.items.map((o) => <li key={o.id} data-testid={`campaign-overlap-${o.id}`}><span className="font-semibold">{o.name}</span> x{o.multiplier} · {fmtIt(o.overlap_from)} → {fmtIt(o.overlap_to)} · <span className={o.wins ? "text-amber-700 dark:text-amber-300 font-semibold" : "text-emerald-700 dark:text-emerald-300"}>{o.wins ? "vince lei" : "vince questa"}</span></li>)}
                </ul>
              </div>
            )}

            <div className="border-t border-zinc-800 pt-3 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Raccolta punti</div>
              <Select value={form.collection_id || "__default__"} onValueChange={(v) => setForm({ ...form, collection_id: v === "__default__" ? "" : v })}>
                <SelectTrigger data-testid="campaign-form-collection" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {collections.map((c) => <SelectItem key={c.id} value={c.is_default ? "__default__" : c.id}>{c.name}{c.is_default ? " (principale)" : ""}</SelectItem>)}
                  {collections.length === 0 && <SelectItem value="__default__">Raccolta principale</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500">Il moltiplicatore vale solo sui punti accreditati su questa raccolta.</p>
            </div>

            <div className="border-t border-zinc-800 pt-3 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cluster & Coupon</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 mb-1 inline-block text-xs">Cluster destinatari</Label>
                  <Select value={form.segment_id || "__none__"} onValueChange={(v) => setForm({ ...form, segment_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="campaign-form-segment" className="bg-zinc-950 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nessuno</SelectItem>
                      {segments.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.member_count ?? 0} clienti</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 mb-1 inline-block text-xs">Coupon da regalare</Label>
                  <Select value={form.coupon_id || "__none__"} onValueChange={(v) => setForm({ ...form, coupon_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="campaign-form-coupon" className="bg-zinc-950 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nessuno</SelectItem>
                      {coupons.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 mb-1 inline-block text-xs">Messaggio WhatsApp per il lancio</Label>
                <Textarea
                  data-testid="campaign-form-wabody"
                  rows={3}
                  placeholder="Ciao {{name}}! Nuova campagna: usa il coupon {{coupon}}…"
                  value={form.wa_body}
                  onChange={(e) => setForm({ ...form, wa_body: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-sm"
                />
                <div className="text-[10px] text-zinc-500 mt-1">
                  Variabili: {"{{name}} {{points}} {{card}} {{coupon}} {{discount}} {{coupon_expiry}}"}
                </div>
              </div>
            </div>

            <div className="border-t border-zinc-800 pt-3 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">📡 Modalità comunicazione</div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { k: "whatsapp", l: "WhatsApp", c: "emerald" },
                  { k: "email", l: "Email", c: "blue" },
                  { k: "both", l: "Entrambi", c: "amber" },
                  { k: "sms", l: "SMS", c: "sky" },
                ].map((opt) => {
                  const on = form.channel === opt.k;
                  return (
                    <button
                      key={opt.k}
                      type="button"
                      data-testid={`campaign-channel-${opt.k}`}
                      onClick={() => setForm({ ...form, channel: opt.k })}
                      className={`h-10 rounded-lg text-xs font-bold border transition ${on ? `bg-${opt.c}-500/20 border-${opt.c}-500/60 text-${opt.c}-200` : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}
                    >
                      {opt.l}
                    </button>
                  );
                })}
              </div>
              {form.channel === "sms" && (() => { const st = smsTemplateStats(form.sms_body || ""); return (
                <div className="rounded-xl border border-sky-500/40 bg-sky-500/5 p-3 space-y-2" data-testid="campaign-sms-block">
                  <Label className="text-zinc-300 mb-1 inline-block text-xs">Testo SMS (inviato con SMS Factor)</Label>
                  <Textarea
                    data-testid="campaign-form-smsbody"
                    rows={3}
                    placeholder="Ciao {{name}}, usa il coupon {{coupon}} entro il {{coupon_expiry}}!"
                    value={form.sms_body}
                    onChange={(e) => setForm({ ...form, sms_body: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-sm"
                  />
                  <div className="flex items-center justify-between text-[11px] font-mono">
                    <span className={st.ok ? "text-zinc-500" : "text-red-300 font-bold"} data-testid="campaign-sms-counter">{st.length}/{st.limit} caratteri · {st.remaining >= 0 ? `${st.remaining} rimasti` : "oltre il limite"}</span>
                    {st.unicode && <span className="text-amber-300" data-testid="campaign-sms-unicode">Caratteri speciali/emoji: limite ridotto a 70</span>}
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    Variabili: {"{{name}} {{points}} {{card}} {{coupon}} {{discount}} {{coupon_expiry}}"} · conteggio stimato con valori di esempio; nomi più lunghi possono sforare e verranno troncati a 160.
                  </div>
                  {form.sms_body && <div className="text-[11px] text-zinc-400 italic border-t border-sky-500/20 pt-2 break-all" data-testid="campaign-sms-preview">Anteprima: {st.preview}</div>}
                  <div className="hidden">
                  </div>
                </div>
              ); })()}
              <div>
                <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-2 text-xs">
                  ⏰ Invio programmato (opzionale)
                </Label>
                <Input
                  data-testid="campaign-scheduled-at"
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 h-10"
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Lascia vuoto per inviare subito dopo aver salvato e cliccato "Lancia". Con data valorizzata la campagna resta in coda e il pulsante Lancia mostrerà l'orario.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
              <div><div className="text-sm font-medium">Attiva</div><div className="text-xs text-zinc-500">Applica il moltiplicatore alle transazioni</div></div>
              <Switch data-testid="campaign-form-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>

            <Button data-testid="campaign-form-submit" onClick={submit} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              {overlapAck && overlap?.loses ? "Salva comunque" : editing ? "Salva" : "Crea campagna"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* LAUNCH RESULT */}
      <Dialog open={!!launchResult} onOpenChange={(o) => !o && setLaunchResult(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Lancio campagna: {launchResult?.campaign?.name}</DialogTitle></DialogHeader>
          <div className="text-sm text-zinc-400 mb-3" data-testid="launch-result-summary">
            {launchResult?.via_spoki
              ? <>📡 Inviata dal numero ufficiale via Spoki: <b className="text-emerald-300">{launchResult?.spoki?.accepted || 0} accettati</b>{launchResult?.spoki?.failed ? <>, <b className="text-red-300">{launchResult.spoki.failed} falliti</b></> : null}{launchResult?.spoki?.duplicate ? `, ${launchResult.spoki.duplicate} già inviati` : ""}. I link sotto restano disponibili come fallback manuale.</>
              : <>{launchResult?.count} messaggi generati. Le prime 3 chat WhatsApp sono state aperte automaticamente.</>}
          </div>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {(launchResult?.messages || []).map((m) => (
              <a key={m.customer_id} href={m.wa_link} target="_blank" rel="noreferrer"
                 className="block p-2 rounded-md border border-zinc-800 hover:bg-zinc-900/50">
                <div className="text-sm font-medium flex items-center justify-between gap-2">
                  <span>{m.customer_name}</span>
                  {m.spoki_state && <span className={`pill text-[10px] ${m.spoki_state === "accepted" ? "pill-mint" : "pill-red"}`}>{m.spoki_state === "accepted" ? "Spoki ✓" : m.spoki_state}</span>}{m.sms_state && <span className={`pill text-[10px] ${m.sms_state === "accepted" ? "pill-sky" : "pill-red"}`} data-testid="launch-sms-state">{m.sms_state === "accepted" ? "SMS ✓" : `SMS ${m.sms_state}`}</span>}
                </div>
                <div className="text-xs text-zinc-500 font-mono">{m.phone}</div>
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ANALYTICS */}
      <Dialog open={!!analytics} onOpenChange={(o) => !o && setAnalytics(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto max-w-3xl">
          <DialogHeader><DialogTitle>Analisi campagna: {analytics?.campaign?.name}</DialogTitle></DialogHeader>
          {analyticsLoading ? (
            <div className="text-zinc-500 py-4">Caricamento…</div>
          ) : !analytics?.current ? (
            <div className="text-zinc-500 py-4">Nessun dato disponibile.</div>
          ) : (
            <div className="space-y-4">
              <div className="text-xs text-zinc-500">
                Periodo: {analytics.window.from} → {analytics.window.to} ({analytics.window.duration_days}gg) ·
                Baseline: {analytics.window.baseline_from} → {analytics.window.baseline_to}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <KPI label="Membri" value={analytics.members} accent="blue" />
                <KPI label="Partecipanti" value={analytics.current.participants} suffix={`/${analytics.members}`} accent="mint" />
                <KPI label="Response rate" value={`${analytics.response_rate}%`} accent="gold" />
                <KPI label="Fatturato" value={`€ ${analytics.current.revenue.toFixed(2)}`} accent="red" />
                <KPI label="Transazioni" value={analytics.current.tx_count} accent="gold" />
                <KPI label="Scontrino medio" value={`€ ${analytics.current.avg_ticket.toFixed(2)}`} accent="mint" />
                <KPI label="Uplift €" value={fmtPct(analytics.uplift.revenue_pct)} accent="red" />
                <KPI label="Uplift tx" value={fmtPct(analytics.uplift.tx_pct)} accent="gold" />
              </div>

              {analytics.spoki && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm" data-testid="analytics-spoki">
                  <div className="font-semibold text-emerald-300 flex items-center gap-2">
                    📡 Consegne WhatsApp ({waLabel})
                  </div>
                  <div className="text-xs text-zinc-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span>Inviati: <strong>{analytics.spoki.accepted}</strong>/{analytics.spoki.sent}</span>
                    <span>✓✓ Consegnati: <strong className="text-emerald-300">{analytics.spoki.delivered}</strong></span>
                    <span>👁 Letti: <strong className="text-amber-300">{analytics.spoki.read}</strong>{analytics.spoki.accepted ? ` (${Math.round(analytics.spoki.read / analytics.spoki.accepted * 100)}%)` : ""}</span>
                    {analytics.spoki.failed > 0 && <span>✗ Falliti: <strong className="text-red-300">{analytics.spoki.failed}</strong></span>}
                    {analytics.spoki.pending > 0 && <span className="text-zinc-500">In attesa stato: {analytics.spoki.pending}</span>}
                  </div>
                </div>
              )}

              {analytics.coupon && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                  <div className="font-semibold text-amber-300 flex items-center gap-2">
                    <Ticket className="h-4 w-4" /> Coupon collegato
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">
                    Assegnati: <strong>{analytics.coupon.assigned}</strong> ·
                    Usati: <strong>{analytics.coupon.used}</strong> ·
                    Sconto totale: <strong>€ {analytics.coupon.discount_total.toFixed(2)}</strong>
                  </div>
                </div>
              )}

              <div>
                <h4 className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Andamento giornaliero
                </h4>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                    <LineChart data={analytics.series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
                      <YAxis stroke="#64748B" fontSize={11} />
                      <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 10 }} />
                      <Line type="monotone" dataKey="revenue" name="€" stroke="#E5383B" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="tx_count" name="tx" stroke="#1E40AF" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <WaSendQueue open={!!waQueue} onClose={() => setWaQueue(null)} messages={waQueue?.messages || []} />
    </div>
  );
}
